<script setup lang="ts">
import { useToast } from '@/composables/toast'
import { getCsrfToken } from '@/helpers/auth'
import { errToStr } from '@/helpers/tools'
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Flow, { type FlowFile } from '@flowjs/flow.js'
import type { Ref } from 'vue'
import { onBeforeUnmount } from 'vue'
import { useTemplateRef } from 'vue'
import type { Beilage } from '@/models/apollon_data/Beilage'

const modelValue = defineModel<boolean>('modelValue')

const {
    title = useI18n().t('upload_doc'),
    text = '',
    target,
    maxWidth = null,
    query,
} = defineProps<{
    title?: string
    text?: string
    target: string | null
    maxWidth?: string | number | null
    query?: object | null
}>()

const chooseBtn = useTemplateRef('chooseBtn')
const progress = ref(false)
const flow: Ref<Flow | null> = ref(null)
const file: Ref<FlowFile | null> = ref(null)

onMounted(() => {
    flow.value = new Flow({
        singleFile: true,
        target: target || '',
        testChunks: false,
        chunkSize: 5 * 1024 * 1024,
        query: query || {},
        headers: {
            'X-CSRF-Token': getCsrfToken(),
        },
    })
    flow.value.on('fileAdded', onFlowFileAdded)
    flow.value.on('fileSuccess', onFlowFileSuccess)
    flow.value.on('fileError', onFlowFileError)

    flow.value.assignBrowse(chooseBtn.value?.$el, false, true, {
        accept: '.doc,.docx,.docm,.pdf,.png,.jpg,.jpeg,.tif,.tiff,.gif,.msg,.xls,.xlsx,.xlsm,.heic',
    })
})

const emit = defineEmits<{
    (e: 'uploadfailed', msg: string): void
    (e: 'uploadsuccess', beilage: Beilage): void
}>()

onBeforeUnmount(() => {
    flow.value?.off()
})

function onFlowFileAdded(flowFile: FlowFile) {
    file.value = flowFile
}
function onFlowFileSuccess(flowFile: FlowFile, msg: string) {
    file.value = null
    progress.value = false
    try {
        const response = JSON.parse(msg)
        const beilageInfo = response.record
        emit('uploadsuccess', beilageInfo)
    } catch (e) {
        useToast().add({ text: errToStr(e), color: 'error' })
    }
}
function onFlowFileError(flowFile: FlowFile, msg: string) {
    file.value = null
    useToast().add({ text: errToStr(msg), color: 'error' })
    emit('uploadfailed', msg)
}
function startUpload() {
    progress.value = true
    flow.value?.upload()
}
</script>

<template>
    <v-dialog v-model:model-value="modelValue" :max-width="maxWidth || undefined" persistent>
        <v-card>
            <v-card-title>{{ title }}</v-card-title>
            <v-card-text v-if="text">
                {{ text }}
            </v-card-text>
            <v-card-text>
                <v-btn
                    ref="chooseBtn"
                    color="primary white--text"
                    justify-center
                    :disabled="progress"
                    prepend-icon="far fa-folder-open"
                >
                    {{ $t('choose_file') }}
                </v-btn>
                <span v-if="file" class="ml-3">{{ file.name }}</span>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    color="red"
                    text
                    :disabled="progress"
                    @click="$emit('update:modelValue', false)"
                >
                    {{ $t('cancel') }}
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="file"
                    color="action white--text"
                    justify-center
                    :loading="progress"
                    @click="startUpload"
                >
                    <v-icon left class="small-icon">fas fa-upload</v-icon>
                    {{ $t('upload_doc') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
